import { Vec3, eq, EPS } from './vec';

/**
 * Flächenbasiertes Modell wie SketchUp: Kanten sind die primären Elemente,
 * Flächen entstehen aus geschlossenen, koplanaren Kantenringen.
 * Alle Koordinaten in Metern, Z zeigt nach oben.
 */

export interface VertexData {
  id: number;
  p: Vec3;
}

export interface EdgeData {
  id: number;
  /** Vertex-IDs */
  a: number;
  b: number;
  /** Optionale Gruppenzugehörigkeit. */
  group?: number;
}

export interface FaceData {
  id: number;
  /** Vertex-IDs des äußeren Rings, gegen den Uhrzeigersinn von außen betrachtet. */
  outer: number[];
  /** Innenkonturen (Löcher), entgegengesetzt gewunden. */
  holes: number[][];
  color?: string;
  /** Optionale Gruppenzugehörigkeit. */
  group?: number;
}

/**
 * Gruppe wie in SketchUp: eigener Kontext, kann weitere Gruppen enthalten.
 * Kanten/Flächen verweisen auf ihre direkte Gruppe (EdgeData.group/FaceData.group).
 */
export interface GroupData {
  id: number;
  /** Übergeordnete Gruppe; undefined = Modell-Ebene. */
  parent?: number;
  name?: string;
  /** Gruppenachsen (rot/grün/blau) — drehen beim Drehen der Gruppe mit, bestimmen den Umriss. */
  axes?: [Vec3, Vec3, Vec3];
  /** Ebenen-Panel: false = ausgeblendet (undefined = eingeblendet). */
  visible?: boolean;
  /** Ebenen-Panel: Deckkraft 0..1 (undefined = 1). */
  opacity?: number;
}

/** Hilfslinie (Maßband/Werkzeug); Segment von a nach b oder unendliche Achsgerade. */
export interface GuideData {
  id: number;
  a: Vec3;
  b: Vec3;
  /** true: unendliche Gerade entlang a→b (z. B. versetzte Achsen-Hilfslinie). */
  infinite?: boolean;
}

export interface ModelJSON {
  vertices: [number, [number, number, number]][];
  edges: [number, number, number, (number | undefined)?][];
  faces: [number, number[], number[][], (string | undefined)?, (number | undefined)?][];
  guides: [number, [number, number, number], [number, number, number], (0 | 1)?][];
  /** [id, parent, name, axes, ebenen] — ebenen optional für ältere Dateien: [sichtbar 0|1, opacity]. */
  groups?: [number, number | null, string | null, [number, number, number][] | null, [0 | 1, number] | null][];
  nextId: number;
  /** Lose Geometrie der Modellebene: [sichtbar 0|1, opacity] — optional für ältere Dateien. */
  loose?: [0 | 1, number];
}

/** Ergebnis von objectAt: loses Element des Kontexts, (Unter-)Gruppe oder außerhalb. */
export type ContextObject = { kind: 'loose' } | { kind: 'group'; id: number } | null;

export class Model {
  vertices = new Map<number, VertexData>();
  edges = new Map<number, EdgeData>();
  faces = new Map<number, FaceData>();
  guides = new Map<number, GuideData>();
  groups = new Map<number, GroupData>();
  nextId = 1;
  /**
   * Geöffnete Gruppe (Bearbeitungskontext, nicht gespeichert). Neue Kanten/Flächen
   * landen in diesem Kontext; nur Geometrie desselben Kontexts verschmilzt miteinander.
   */
  editContext: number | undefined = undefined;
  /** Ebenen-Panel: lose Geometrie der Modellebene ein-/ausgeblendet mit Deckkraft. */
  looseVisible = true;
  looseOpacity = 1;

  newId(): number {
    return this.nextId++;
  }

  vertexPosition(id: number): Vec3 {
    const v = this.vertices.get(id);
    if (!v) throw new Error(`Vertex ${id} existiert nicht`);
    return v.p;
  }

  edgePoints(e: EdgeData): [Vec3, Vec3] {
    return [this.vertexPosition(e.a), this.vertexPosition(e.b)];
  }

  facePoints(f: FaceData): Vec3[] {
    return f.outer.map((id) => this.vertexPosition(id));
  }

  addVertex(p: Vec3, id = this.newId()): VertexData {
    const v: VertexData = { id, p };
    this.vertices.set(id, v);
    return v;
  }

  /** Legt eine Kante an; duplizierte oder null-lange Kanten werden ignoriert (Rückgabe null). */
  addEdge(a: number, b: number, id = this.newId()): EdgeData | null {
    if (a === b) return null;
    const pa = this.vertexPosition(a);
    const pb = this.vertexPosition(b);
    if (eq(pa, pb, EPS)) return null;
    const existing = this.findEdgeBetween(a, b);
    if (existing) return existing;
    const e: EdgeData = { id, a, b, group: this.editContext };
    this.edges.set(id, e);
    return e;
  }

  findEdgeBetween(a: number, b: number): EdgeData | null {
    for (const e of this.edges.values()) {
      if ((e.a === a && e.b === b) || (e.a === b && e.b === a)) return e;
    }
    return null;
  }

  /**
   * Legt eine Fläche aus einem bereits existierenden Vertexring an.
   * Ringe mit <3 Punkten werden abgelehnt.
   */
  addFace(outer: number[], holes: number[][] = [], id = this.newId(), color?: string): FaceData | null {
    if (outer.length < 3) return null;
    const f: FaceData = { id, outer: [...outer], holes: holes.map((h) => [...h]), color, group: this.editContext };
    this.faces.set(id, f);
    return f;
  }

  addGuide(a: Vec3, b: Vec3, infinite = false, id = this.newId()): GuideData {
    const g: GuideData = { id, a, b, infinite: infinite || undefined };
    this.guides.set(id, g);
    return g;
  }

  /** Löscht eine Kante; Flächen, die die Kante verwenden, fallen mit weg. */
  deleteEdge(edgeId: number): void {
    const e = this.edges.get(edgeId);
    if (!e) return;
    for (const f of [...this.faces.values()]) {
      if (faceUsesEdge(f, e.a, e.b)) this.faces.delete(f.id);
    }
    this.edges.delete(edgeId);
    this.pruneVertex(e.a);
    this.pruneVertex(e.b);
  }

  /** Löscht eine Fläche; Kanten bleiben erhalten (wie SketchUp). */
  deleteFace(faceId: number): void {
    this.faces.delete(faceId);
  }

  deleteGuide(guideId: number): void {
    this.guides.delete(guideId);
  }

  /** Entfernt einen Vertex, wenn er von keiner Kante mehr referenziert wird. */
  pruneVertex(vertexId: number): void {
    for (const e of this.edges.values()) {
      if (e.a === vertexId || e.b === vertexId) return;
    }
    this.vertices.delete(vertexId);
  }

  /** Vertices, die von keiner Kante referenziert werden, entfernen. */
  pruneOrphanVertices(): void {
    const used = new Set<number>();
    for (const e of this.edges.values()) { used.add(e.a); used.add(e.b); }
    for (const id of [...this.vertices.keys()]) {
      if (!used.has(id)) this.vertices.delete(id);
    }
  }

  /** Alle Vertex-IDs, die von den gegebenen Kanten/Flächen genutzt werden. */
  verticesOf(edgeIds: Iterable<number>, faceIds: Iterable<number>): Set<number> {
    const out = new Set<number>();
    for (const id of edgeIds) {
      const e = this.edges.get(id);
      if (e) { out.add(e.a); out.add(e.b); }
    }
    for (const id of faceIds) {
      const f = this.faces.get(id);
      if (f) {
        for (const v of f.outer) out.add(v);
        for (const h of f.holes) for (const v of h) out.add(v);
      }
    }
    return out;
  }

  /** Flächen, die einen der gegebenen Vertices berühren. */
  facesTouchingVertices(vertexIds: Iterable<number>): FaceData[] {
    const set = new Set(vertexIds);
    const out: FaceData[] = [];
    for (const f of this.faces.values()) {
      if (f.outer.some((v) => set.has(v))) out.push(f);
    }
    return out;
  }

  /** Tiefenkopie — Basis für Undo-Snapshots. */
  clone(): Model {
    return Model.fromJSON(this.toJSON());
  }

  // ---------- Gruppen-Hierarchie ----------

  /** Übergeordnete Gruppe (undefined = Modell-Ebene). */
  parentOf(gid: number): number | undefined {
    return this.groups.get(gid)?.parent;
  }

  /** true, wenn `g` gleich `ancestor` ist oder darin (verschachtelt) liegt. undefined = Modell-Ebene. */
  isInside(g: number | undefined, ancestor: number | undefined): boolean {
    if (ancestor === undefined) return true;
    let cur = g;
    const guard = new Set<number>();
    while (cur !== undefined && !guard.has(cur)) {
      if (cur === ancestor) return true;
      guard.add(cur);
      cur = this.parentOf(cur);
    }
    return false;
  }

  /**
   * Welches Objekt des Kontexts `ctx` enthält ein Element der Gruppe `g`?
   * loose = direkt im Kontext, group = die Untergruppe auf Kontext-Ebene, null = außerhalb.
   */
  objectAt(g: number | undefined, ctx: number | undefined = this.editContext): ContextObject {
    if (g === ctx) return { kind: 'loose' };
    let cur = g;
    const guard = new Set<number>();
    while (cur !== undefined && !guard.has(cur)) {
      guard.add(cur);
      const parent = this.parentOf(cur);
      if (parent === ctx) return { kind: 'group', id: cur };
      cur = parent;
    }
    return null;
  }

  /** Gruppe plus alle darin verschachtelten Gruppen. */
  subtreeGroups(gid: number): Set<number> {
    const out = new Set<number>([gid]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const g of this.groups.values()) {
        if (g.parent !== undefined && out.has(g.parent) && !out.has(g.id)) {
          out.add(g.id);
          grew = true;
        }
      }
    }
    return out;
  }

  /** Alle Kanten-/Flächen-IDs einer Gruppe inkl. verschachtelter Gruppen. */
  entitiesInGroup(groupId: number): { edges: number[]; faces: number[] } {
    const sub = this.subtreeGroups(groupId);
    const edges: number[] = [];
    const faces: number[] = [];
    for (const e of this.edges.values()) if (e.group !== undefined && sub.has(e.group)) edges.push(e.id);
    for (const f of this.faces.values()) if (f.group !== undefined && sub.has(f.group)) faces.push(f.id);
    return { edges, faces };
  }

  /** Legt einen Gruppen-Datensatz an. */
  addGroup(parent: number | undefined, id = this.newId()): GroupData {
    const g: GroupData = { id, parent };
    this.groups.set(id, g);
    return g;
  }

  /** true, wenn das Element im aktuellen Bearbeitungskontext einzeln bearbeitbar ist. */
  isEditable(entity: { group?: number } | undefined): boolean {
    return entity !== undefined && entity.group === this.editContext;
  }

  /** Vertices, die zu losen Elementen des Kontexts gehören (oder an keiner Kante hängen). */
  contextVertexIds(ctx: number | undefined = this.editContext): Set<number> {
    const used = new Set<number>();
    const inCtx = new Set<number>();
    for (const e of this.edges.values()) {
      used.add(e.a); used.add(e.b);
      if (e.group === ctx) { inCtx.add(e.a); inCtx.add(e.b); }
    }
    for (const f of this.faces.values()) {
      if (f.group !== ctx) continue;
      for (const v of f.outer) inCtx.add(v);
      for (const h of f.holes) for (const v of h) inCtx.add(v);
    }
    for (const id of this.vertices.keys()) if (!used.has(id)) inCtx.add(id);
    return inCtx;
  }

  /** Gruppen auf Kontext-Ebene, die in der Auswahl vorkommen. */
  groupsAt(edgeIds: Iterable<number>, faceIds: Iterable<number>, ctx: number | undefined = this.editContext): Set<number> {
    const out = new Set<number>();
    const take = (g: number | undefined) => {
      const o = this.objectAt(g, ctx);
      if (o?.kind === 'group') out.add(o.id);
    };
    for (const id of edgeIds) take(this.edges.get(id)?.group);
    for (const id of faceIds) take(this.faces.get(id)?.group);
    return out;
  }

  /**
   * Erweitert eine Auswahl auf ganze Objekte des Kontexts: Teile geschlossener
   * Gruppen werden durch die ganze Gruppe ersetzt, Elemente außerhalb des Kontexts entfallen.
   */
  expandToGroups(edgeIds: Iterable<number>, faceIds: Iterable<number>): { edges: Set<number>; faces: Set<number> } {
    const edges = new Set<number>();
    const faces = new Set<number>();
    const groups = new Set<number>();
    for (const id of edgeIds) {
      const e = this.edges.get(id);
      const o = e ? this.objectAt(e.group) : null;
      if (o?.kind === 'loose') edges.add(id);
      else if (o?.kind === 'group') groups.add(o.id);
    }
    for (const id of faceIds) {
      const f = this.faces.get(id);
      const o = f ? this.objectAt(f.group) : null;
      if (o?.kind === 'loose') faces.add(id);
      else if (o?.kind === 'group') groups.add(o.id);
    }
    for (const g of groups) {
      const ent = this.entitiesInGroup(g);
      ent.edges.forEach((id) => edges.add(id));
      ent.faces.forEach((id) => faces.add(id));
    }
    return { edges, faces };
  }

  /**
   * Gruppiert die Auswahl im aktuellen Kontext wie SketchUp: lose Elemente
   * (plus Randkanten gewählter Flächen) und gewählte Untergruppen wandern in eine
   * neue Gruppe; geteilte Eckpunkte werden aufgetrennt. Liefert die Gruppen-ID.
   */
  groupEntities(edgeIds: Iterable<number>, faceIds: Iterable<number>): number {
    const ctx = this.editContext;
    const edgeList = [...edgeIds];
    const faceList = [...faceIds];
    const childGroups = this.groupsAt(edgeList, faceList, ctx);
    const gid = this.addGroup(ctx).id;
    const looseFaces = faceList.filter((id) => this.faces.get(id)?.group === ctx);
    const looseEdges = new Set(edgeList.filter((id) => this.edges.get(id)?.group === ctx));
    for (const id of looseFaces) {
      const f = this.faces.get(id)!;
      for (const loop of [f.outer, ...f.holes]) {
        for (let i = 0; i < loop.length; i++) {
          const e = this.findEdgeBetween(loop[i]!, loop[(i + 1) % loop.length]!);
          if (e && e.group === ctx) looseEdges.add(e.id);
        }
      }
    }
    // Nur die Auswahl wandert in die Gruppe: Kanten, die auch eine nicht gewählte
    // Fläche begrenzen, werden kopiert — der Nachbar behält seine Kante (wie SketchUp)
    const grouped = new Set(looseFaces);
    const otherFaces = [...this.faces.values()].filter((f) => f.group === ctx && !grouped.has(f.id));
    for (const id of looseEdges) {
      const e = this.edges.get(id)!;
      if (otherFaces.some((f) => faceUsesEdge(f, e.a, e.b))) {
        const copyId = this.newId();
        this.edges.set(copyId, { id: copyId, a: e.a, b: e.b, group: gid });
      } else {
        e.group = gid;
      }
    }
    for (const id of looseFaces) this.faces.get(id)!.group = gid;
    for (const cg of childGroups) this.groups.get(cg)!.parent = gid;
    this.isolateGroup(gid);
    return gid;
  }

  /** Trennt Eckpunkte, die eine Gruppe (inkl. Untergruppen) mit anderer Geometrie teilt. */
  isolateGroup(gid: number): void {
    const sub = this.subtreeGroups(gid);
    const isIn = (g: number | undefined) => g !== undefined && sub.has(g);
    const inside = new Set<number>();
    const outside = new Set<number>();
    for (const e of this.edges.values()) {
      const target = isIn(e.group) ? inside : outside;
      target.add(e.a); target.add(e.b);
    }
    for (const f of this.faces.values()) {
      const target = isIn(f.group) ? inside : outside;
      for (const v of f.outer) target.add(v);
      for (const h of f.holes) for (const v of h) target.add(v);
    }
    const remap = new Map<number, number>();
    for (const v of inside) {
      if (outside.has(v)) remap.set(v, this.addVertex(this.vertexPosition(v)).id);
    }
    if (remap.size === 0) return;
    const m = (v: number) => remap.get(v) ?? v;
    for (const e of this.edges.values()) {
      if (!isIn(e.group)) continue;
      e.a = m(e.a); e.b = m(e.b);
    }
    for (const f of this.faces.values()) {
      if (!isIn(f.group)) continue;
      f.outer = f.outer.map(m);
      f.holes = f.holes.map((h) => h.map(m));
    }
  }

  /**
   * Löst eine Gruppe auf (SketchUp „Auflösen“): Inhalt wandert eine Ebene nach oben,
   * lose Elemente verschmelzen dort mit deckungsgleichen Eckpunkten/Kanten.
   * Liefert die Elemente des Inhalts.
   */
  explodeGroup(gid: number): { edges: number[]; faces: number[] } {
    const g = this.groups.get(gid);
    if (!g) return { edges: [], faces: [] };
    const parent = g.parent;
    const moved = new Set<number>();
    for (const e of this.edges.values()) {
      if (e.group !== gid) continue;
      e.group = parent;
      moved.add(e.a); moved.add(e.b);
    }
    for (const f of this.faces.values()) {
      if (f.group !== gid) continue;
      f.group = parent;
      f.outer.forEach((v) => moved.add(v));
      f.holes.forEach((h) => h.forEach((v) => moved.add(v)));
    }
    const children: number[] = [];
    for (const c of this.groups.values()) {
      if (c.parent === gid) { c.parent = parent; children.push(c.id); }
    }
    this.groups.delete(gid);
    this.mergeCoincidentVertices(moved, parent);

    const edges: number[] = [];
    const faces: number[] = [];
    for (const e of this.edges.values()) if (e.group === parent && (moved.has(e.a) || moved.has(e.b))) edges.push(e.id);
    for (const f of this.faces.values()) if (f.group === parent && f.outer.some((v) => moved.has(v))) faces.push(f.id);
    for (const c of children) {
      const ent = this.entitiesInGroup(c);
      edges.push(...ent.edges);
      faces.push(...ent.faces);
    }
    return { edges, faces };
  }

  /** Verschmilzt die gegebenen Vertices mit deckungsgleichen Vertices der Ebene `ctx`. */
  private mergeCoincidentVertices(vertexIds: Set<number>, ctx: number | undefined): void {
    const pool = this.contextVertexIds(ctx);
    const remap = new Map<number, number>();
    for (const id of vertexIds) {
      const p = this.vertexPosition(id);
      for (const other of pool) {
        if (other === id || vertexIds.has(other)) continue;
        const q = this.vertices.get(other)?.p;
        if (q && eq(p, q, 1e-4)) { remap.set(id, other); break; }
      }
    }
    if (remap.size === 0) return;
    const m = (v: number) => remap.get(v) ?? v;
    const seen = new Map<string, number>();
    for (const e of [...this.edges.values()]) {
      if (e.group !== ctx) continue;
      e.a = m(e.a); e.b = m(e.b);
      const key = e.a < e.b ? `${e.a}:${e.b}` : `${e.b}:${e.a}`;
      if (e.a === e.b || seen.has(key)) this.edges.delete(e.id);
      else seen.set(key, e.id);
    }
    for (const f of this.faces.values()) {
      if (f.group !== ctx) continue;
      f.outer = f.outer.map(m);
      f.holes = f.holes.map((h) => h.map(m));
    }
    for (const id of remap.keys()) this.vertices.delete(id);
    // Doppelte Flächen (gleicher Ring) entfernen
    const faceKeys = new Set<string>();
    for (const f of [...this.faces.values()]) {
      if (f.group !== ctx) continue;
      const key = [...f.outer].sort((a, b) => a - b).join(',');
      if (faceKeys.has(key)) this.faces.delete(f.id);
      else faceKeys.add(key);
    }
  }

  /** Gruppenachsen (Standard: Weltachsen). */
  groupAxes(gid: number): [Vec3, Vec3, Vec3] {
    return this.groups.get(gid)?.axes ?? [{ x: 1, y: 0, z: 0 }, { x: 0, y: 1, z: 0 }, { x: 0, y: 0, z: 1 }];
  }

  /** Dreht die Achsen der Gruppen (inkl. Untergruppen) mit — nach dem Drehen ihrer Geometrie. */
  rotateGroupAxes(groupIds: Iterable<number>, rotate: (v: Vec3) => Vec3): void {
    for (const gid of groupIds) {
      for (const sg of this.subtreeGroups(gid)) {
        const g = this.groups.get(sg);
        if (!g) continue;
        const [x, y, z] = this.groupAxes(sg);
        g.axes = [rotate(x), rotate(y), rotate(z)];
      }
    }
  }

  // ---------- Ebenen (Sichtbarkeit / Deckkraft) ----------

  /** Oberste Vorfahrgruppe (undefined = lose Modellebene) — ihre Ebenen-Eigenschaften gelten. */
  topLevelGroup(g: number | undefined): number | undefined {
    let cur = g;
    const guard = new Set<number>();
    while (cur !== undefined && !guard.has(cur)) {
      guard.add(cur);
      const parent = this.parentOf(cur);
      if (parent === undefined) return cur;
      cur = parent;
    }
    return undefined;
  }

  /** true, wenn das Element angezeigt wird (oberste Gruppe eingeblendet bzw. lose Ebene sichtbar). */
  isEntityVisible(group: number | undefined): boolean {
    const top = this.topLevelGroup(group);
    if (top === undefined) return this.looseVisible;
    return this.groups.get(top)?.visible !== false;
  }

  /** Effektive Deckkraft des Elements (0..1) über seine oberste Gruppe bzw. die lose Ebene. */
  entityOpacity(group: number | undefined): number {
    const top = this.topLevelGroup(group);
    if (top === undefined) return this.looseOpacity;
    return this.groups.get(top)?.opacity ?? 1;
  }

  /** Blendet die oberste Gruppe des Elements (bzw. die lose Ebene) ein/aus. */
  setEntityVisible(group: number | undefined, visible: boolean): void {
    const top = this.topLevelGroup(group);
    if (top === undefined) this.looseVisible = visible;
    else {
      const g = this.groups.get(top);
      if (g) g.visible = visible;
    }
  }

  /**
   * Repariert Modelle aus älteren Versionen: Gruppen teilten Eckpunkte mit ihrer
   * Umgebung, und beim Gruppieren wanderten gemeinsame Kanten in die Gruppe.
   * Danach sind Gruppen isoliert und jede Fläche hat Randkanten in ihrem Kontext.
   */
  repairGroups(): void {
    for (const gid of this.groups.keys()) this.isolateGroup(gid);
    const key = (a: number, b: number, g: number | undefined) => (a < b ? `${a}:${b}:${g}` : `${b}:${a}:${g}`);
    const existing = new Set<string>();
    for (const e of this.edges.values()) existing.add(key(e.a, e.b, e.group));
    for (const f of this.faces.values()) {
      for (const loop of [f.outer, ...f.holes]) {
        for (let i = 0; i < loop.length; i++) {
          const a = loop[i]!, b = loop[(i + 1) % loop.length]!;
          if (a === b || existing.has(key(a, b, f.group))) continue;
          const id = this.newId();
          this.edges.set(id, { id, a, b, group: f.group });
          existing.add(key(a, b, f.group));
        }
      }
    }
  }

  /** Entfernt Gruppen ohne Inhalt (auch keine Untergruppen mit Inhalt). */
  pruneEmptyGroups(): void {
    const used = new Set<number>();
    for (const e of this.edges.values()) if (e.group !== undefined) used.add(e.group);
    for (const f of this.faces.values()) if (f.group !== undefined) used.add(f.group);
    const alive = new Set<number>();
    for (const g of used) {
      let cur: number | undefined = g;
      while (cur !== undefined && !alive.has(cur)) {
        alive.add(cur);
        cur = this.parentOf(cur);
      }
    }
    for (const id of [...this.groups.keys()]) {
      if (!alive.has(id) && id !== this.editContext) this.groups.delete(id);
    }
  }

  toJSON(): ModelJSON {
    return {
      vertices: [...this.vertices.values()].map((v) => [v.id, [v.p.x, v.p.y, v.p.z]] as [number, [number, number, number]]),
      edges: [...this.edges.values()].map((e) => [e.id, e.a, e.b, e.group] as [number, number, number, number | undefined]),
      faces: [...this.faces.values()].map(
        (f) => [f.id, f.outer, f.holes, f.color, f.group] as [number, number[], number[][], string | undefined, number | undefined]
      ),
      guides: [...this.guides.values()].map(
        (g) => [g.id, [g.a.x, g.a.y, g.a.z], [g.b.x, g.b.y, g.b.z], g.infinite ? 1 : 0] as [number, [number, number, number], [number, number, number], 0 | 1]
      ),
      groups: [...this.groups.values()].map((g) => [
        g.id,
        g.parent ?? null,
        g.name ?? null,
        g.axes ? g.axes.map((a) => [a.x, a.y, a.z] as [number, number, number]) : null,
        g.visible === false || g.opacity !== undefined ? [g.visible === false ? 0 : 1, g.opacity ?? 1] as [0 | 1, number] : null
      ] as [number, number | null, string | null, [number, number, number][] | null, [0 | 1, number] | null]),
      nextId: this.nextId,
      loose: this.looseVisible && this.looseOpacity === 1 ? undefined : [this.looseVisible ? 1 : 0, this.looseOpacity] as [0 | 1, number]
    };
  }

  static fromJSON(json: ModelJSON): Model {
    const m = new Model();
    for (const [id, p] of json.vertices) m.vertices.set(id, { id, p: { x: p[0]!, y: p[1]!, z: p[2]! } });
    // group ?? undefined: JSON kennt kein undefined — Tupel kommen mit null an
    // (Dateien, Sync-Ops, Persistenz); null wäre sonst eine Phantom-Gruppe.
    for (const [id, a, b, group] of json.edges) m.edges.set(id, { id, a, b, group: group ?? undefined });
    for (const [id, outer, holes, color, group] of json.faces) {
      m.faces.set(id, { id, outer, holes, color, group: group ?? undefined });
    }
    for (const [id, a, b, infinite] of json.guides) {
      m.guides.set(id, {
        id,
        a: { x: a[0]!, y: a[1]!, z: a[2]! },
        b: { x: b[0]!, y: b[1]!, z: b[2]! },
        infinite: infinite ? true : undefined
      });
    }
    for (const [id, parent, name, axes, layers] of json.groups ?? []) {
      m.groups.set(id, {
        id,
        parent: parent ?? undefined,
        name: name ?? undefined,
        axes: axes ? (axes.map((a) => ({ x: a[0]!, y: a[1]!, z: a[2]! })) as [Vec3, Vec3, Vec3]) : undefined,
        visible: layers ? layers[0] !== 0 : undefined,
        opacity: layers ? layers[1] : undefined
      });
    }
    if (json.loose) {
      m.looseVisible = json.loose[0] !== 0;
      m.looseOpacity = json.loose[1] ?? 1;
    }
    // Ältere Dateien: Gruppen nur als Markierung an Kanten/Flächen → Datensätze ergänzen
    for (const ent of [...m.edges.values(), ...m.faces.values()]) {
      if (ent.group !== undefined && !m.groups.has(ent.group)) m.groups.set(ent.group, { id: ent.group });
    }
    m.nextId = json.nextId;
    m.repairGroups();
    return m;
  }
}

/** true, wenn die Fläche (Ring oder Loch) die Kante a–b als Teilstrecke enthält. */
export function faceUsesEdge(f: FaceData, a: number, b: number): boolean {
  const loops = [f.outer, ...f.holes];
  for (const loop of loops) {
    for (let i = 0; i < loop.length; i++) {
      const v0 = loop[i]!;
      const v1 = loop[(i + 1) % loop.length]!;
      if ((v0 === a && v1 === b) || (v0 === b && v1 === a)) return true;
    }
  }
  return false;
}
